import React, { useEffect, useState } from "react";
import "../styles/Contact.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import UserQueries from "./UserQueries";
import {
    FiMapPin,
    FiPhone,
    FiMail,
    FiChevronRight,
    FiAward
} from "react-icons/fi";
import { AiOutlineWhatsApp } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API_BASE_URL from "../config";

const Contact = () => {
    const [siteSettings, setSiteSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [randomSolicitors, setRandomSolicitors] = useState([]);
    const [solicitorLoading, setSolicitorLoading] = useState(true);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "",
        message: ""
    });

    const [submitting, setSubmitting] = useState(false);

    /* ==============================
       LOAD CONTACT INFO FROM DB
    ============================== */
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/site-settings`);
                const data = await res.json();
                setSiteSettings(data);
            } catch (err) {
                console.error("❌ Failed to load contact info:", err);
                toast.error("Failed to load contact information");
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    /* ==============================
       LOAD RANDOM SOLICITORS (LIMIT 3)
    ============================== */
    useEffect(() => {
        const fetchRandomSolicitors = async () => {
            try {
                setSolicitorLoading(true);
                const res = await fetch(`${API_BASE_URL}/api/advocates/random?limit=3`);
                const data = await res.json();

                if (data && Array.isArray(data)) {
                    setRandomSolicitors(data);
                }
            } catch (err) {
                console.error("❌ Failed to load random solicitors:", err);
            } finally {
                setSolicitorLoading(false);
            }
        };

        fetchRandomSolicitors();
    }, []);

    /* ==============================
       FORM HANDLERS
    ============================== */
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (submitting) return;

        setSubmitting(true);

        try {
            const res = await fetch(`${API_BASE_URL}/api/contact`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(formData)
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || "Failed to send message");
            }

            // ✅ Success Toast
            toast.success("Your message has been sent successfully!");

            // Reset form
            setFormData({
                name: "",
                email: "",
                subject: "",
                message: ""
            });

        } catch (err) {
            console.error("❌ Contact Submit Error:", err);
            toast.error(err.message || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    /* ==============================
       HANDLE SOLICITOR CLICK
    ============================== */
    const handleSolicitorClick = (solicitorId) => {
        navigate(`/solicitor/${solicitorId}`);
    };

    if (loading) {
        return (
            <>
                <Header />
                <div style={{ textAlign: "center", padding: "120px 0" }}>
                    Loading contact information...
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />

            <div className="contact-page">

                {/* ===== HEADER SECTION ===== */}
                <div className="contact-hero">
                    <h1>Contact <span>Justivon</span></h1>
                    <p>
                        Have questions or need legal assistance?
                        Our team is here to help you.
                    </p>
                </div>

                {/* ===== CONTENT SECTION ===== */}
                <div className="contact-container">

                    {/* LEFT: CONTACT INFO */}
                    <div className="contact-info-card">

                        <h3>Contact Information</h3>

                        <div className="contact-info-item">
                            <FiMapPin />
                            <div>
                                <h4>Address</h4>
                                <p>{siteSettings?.contactAddress || "Address not configured"}</p>
                            </div>
                        </div>

                        <div className="contact-info-item">
                            <FiPhone />
                            <div>
                                <h4>Phone</h4>
                                {siteSettings?.contactPhone ? (
                                    <a href={`tel:${siteSettings.contactPhone}`}>
                                        {siteSettings.contactPhone}
                                    </a>
                                ) : (
                                    <p>Phone not configured</p>
                                )}
                            </div>
                        </div>

                        <div className="contact-info-item">
                            <AiOutlineWhatsApp />
                            <div>
                                <h4>WhatsApp</h4>
                                {siteSettings?.contactAlternatePhone ? (
                                    <a
                                        href={`https://wa.me/${siteSettings.contactAlternatePhone}?text=Hello%20Justivon%2C%20I%20need%20assistance`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {siteSettings.contactAlternatePhone}
                                    </a>
                                ) : (
                                    <p>WhatsApp not configured</p>
                                )}
                            </div>
                        </div>

                        <div className="contact-info-item">
                            <FiMail />
                            <div>
                                <h4>Email</h4>
                                {siteSettings?.contactEmail ? (
                                    <a href={`mailto:${siteSettings.contactEmail}`}>
                                        {siteSettings.contactEmail}
                                    </a>
                                ) : (
                                    <p>Email not configured</p>
                                )}
                            </div>
                        </div>

                    </div>

                    {/* RIGHT: CONTACT FORM */}
                    <div className="contact-form-card">

                        <h3>Send Us a Message</h3>

                        <form onSubmit={handleSubmit} className="contact-form">

                            <div className="form-row">
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Full Name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />

                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email Address"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <input
                                type="text"
                                name="subject"
                                placeholder="Subject"
                                value={formData.subject}
                                onChange={handleChange}
                                required
                            />

                            <textarea
                                name="message"
                                rows="6"
                                placeholder="Your Message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                            ></textarea>

                            <button type="submit" disabled={submitting}>
                                {submitting ? "Sending..." : "Send Message"}
                            </button>

                        </form>
                    </div>

                </div>

                {/* ===== SEPARATE MAP AND SOLICITORS SECTIONS ===== */}
                <div className="map-solicitors-container">

                    {/* MAP SECTION - SEPARATE CARD */}
                    {siteSettings?.googleMapEmbedUrl && (
                        <div className="map-section-card">
                            <div className="map-section-header">
                                <h2>Find Us Here</h2>
                            </div>

                            <div className="contact-map-card">
                                <iframe
                                    src={siteSettings.googleMapEmbedUrl}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Office Location"
                                ></iframe>
                            </div>
                        </div>
                    )}

                    {/* SOLICITORS SECTION - SEPARATE CARD */}
                    <div className="solicitors-section-card">
                        <div className="solicitors-header">
                            <h2>Legal Experts</h2>
                            <p className="solicitors-subtitle">
                                Connect with legal experts
                            </p>
                        </div>

                        {solicitorLoading ? (
                            <div className="solicitors-loading">
                                <div className="loading-spinner"></div>
                                <p>Loading solicitors...</p>
                            </div>
                        ) : randomSolicitors.length > 0 ? (
                            <div className="solicitors-list">
                                {randomSolicitors.map((solicitor, index) => (
                                    <div
                                        key={solicitor._id || index}
                                        className="solicitor-item"
                                        onClick={() => handleSolicitorClick(solicitor._id)}
                                    >
                                        <div className="solicitor-avatar-small">
                                            {solicitor.profile?.photo ? (
                                                <img
                                                    src={solicitor.profile.photo}
                                                    alt={solicitor.name}
                                                    className="solicitor-avatar-img"
                                                />
                                            ) : (
                                                <div className="solicitor-avatar-fallbacks">
                                                    {solicitor.name?.charAt(0) || "S"}
                                                </div>
                                            )}
                                        </div>

                                        <div className="solicitor-details-small">
                                            <h4 className="solicitor-name-small">
                                                {solicitor.name || "Solicitor"}
                                            </h4>
                                            <div className="solicitor-level-badge">
                                                <FiAward size={12} />
                                                <span>{solicitor.profile?.advocateLevel}</span>
                                            </div>
                                        </div>

                                        <div className="solicitor-arrow">
                                            <FiChevronRight size={18} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="solicitors-empty">
                                <p>No solicitors available at the moment.</p>
                                <a href="/search" className="browse-all-btn">
                                    Browse All Solicitors
                                </a>
                            </div>
                        )}

                        <div className="solicitors-footer">
                            <a href="/search" className="view-all-solicitors">
                                View All Solicitors
                            </a>
                        </div>
                    </div>

                </div>

            </div>
            <UserQueries />
            <Footer />
        </>
    );
};

export default Contact;